import React, { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleIcon from '@mui/icons-material/People';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LogoutIcon from '@mui/icons-material/Logout';
import logo from '../assets/images/streetpatrol-logo.png';
import '../styles/Layout.css';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, admin, logout, isVendor, isAdmin } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className="layout">
      {/* Hamburger Button - Only visible on mobile/tablet */}
      <button
        className="hamburger-button"
        onClick={toggleSidebar}
        aria-label="Toggle menu"
      >
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
        <span className="hamburger-line"></span>
      </button>

      {/* Overlay - For closing sidebar on mobile */}
      {isSidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={closeSidebar}
        ></div>
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}>
        <button
          className="sidebar-close-button"
          onClick={closeSidebar}
          aria-label="Close menu"
        >
          ✕
        </button>

        <div className="sidebar-header">
          <img src={logo} alt="StreetPatrol Logo" className="sidebar-logo" />
          <p className="sidebar-subtitle">{isVendor ? 'Vendor Panel' : 'Admin Panel'}</p>
        </div>

        <nav className="sidebar-nav">
          {isVendor ? (
            <>
              {/* Vendor Navigation */}
              <Link to="/" className={isActive('/') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <DashboardIcon className="nav-icon" />
                <span>Dashboard</span>
              </Link>

              <Link to="/vendor-fleet" className={isActive('/vendor-fleet') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <DirectionsCarIcon className="nav-icon" />
                <span>My Fleet</span>
              </Link>

              <Link to="/vendor-jobs" className={isActive('/vendor-jobs') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <AssignmentIcon className="nav-icon" />
                <span>Jobs</span>
              </Link>

              <Link to="/vendor-earnings" className={isActive('/vendor-earnings') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <AttachMoneyIcon className="nav-icon" />
                <span>Earnings</span>
              </Link>
            </>
          ) : (
            <>
              {/* Admin Navigation */}
              <Link to="/" className={isActive('/') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <DashboardIcon className="nav-icon" />
                <span>Dashboard</span>
              </Link>

              <Link to="/drivers" className={isActive('/drivers') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <DirectionsCarIcon className="nav-icon" />
                <span>Drivers</span>
              </Link>

              <Link to="/jobs" className={isActive('/jobs') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <AssignmentIcon className="nav-icon" />
                <span>Jobs</span>
              </Link>

              <Link to="/jobs/create" className={isActive('/jobs/create') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <AddCircleOutlineIcon className="nav-icon" />
                <span>Create Job</span>
              </Link>

              <Link to="/pricing" className={isActive('/pricing') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <AttachMoneyIcon className="nav-icon" />
                <span>Pricing</span>
              </Link>

              <Link to="/offers" className={isActive('/offers') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <LocalOfferIcon className="nav-icon" />
                <span>Offers</span>
              </Link>

              <Link to="/withdrawals" className={isActive('/withdrawals') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <AccountBalanceIcon className="nav-icon" />
                <span>Withdrawals</span>
              </Link>

              <Link to="/admins" className={isActive('/admins') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <PeopleIcon className="nav-icon" />
                <span>Admins</span>
              </Link>

              <Link to="/vendors" className={isActive('/vendors') ? 'nav-item active' : 'nav-item'} onClick={closeSidebar}>
                <StorefrontIcon className="nav-icon" />
                <span>Vendors</span>
              </Link>
            </>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="admin-info">
            <div className="admin-avatar">
              {admin?.firstName?.charAt(0)}{admin?.lastName?.charAt(0)}
            </div>
            <div className="admin-details">
              <p className="admin-name">{admin?.firstName} {admin?.lastName}</p>
              <p className="admin-email">{admin?.email}</p>
            </div>
          </div>
          <button onClick={logout} className="logout-button">
            <LogoutIcon className="logout-icon" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <div className="content-wrapper">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
